import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { isAllBranchesRole } from '../utils/auth';
import { getCurrentUser } from '../utils/auth';
import { useToast } from './ToastContext';
import { useAutoSync } from '../hooks/useAutoSync';

const RefreshIcon = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10" />
    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
  </svg>
);

const ALL_BRANCHES_VALUE = '';

function Dashboard({ companyId }) {
  const currentUser = getCurrentUser();
  const canSelectAllBranches = isAllBranchesRole(currentUser?.role);
  
  // Toast notifications
  const { showToast } = useToast();
  
  // Auto-sync monitoring
  useAutoSync(true);

  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [topSalespersons, setTopSalespersons] = useState([]);
  const [topCustomers, setTopCustomers] = useState([]);
  const [branchSummary, setBranchSummary] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(companyId ? String(companyId) : '');
  const [loading, setLoading] = useState(true);
  const [syncingCustomers, setSyncingCustomers] = useState(false);
  const [syncingLedger, setSyncingLedger] = useState(false);
  const [isFullSyncing, setIsFullSyncing] = useState(false);
  const [isSyncingReports, setIsSyncingReports] = useState(false);
  const [isSyncingARAging, setIsSyncingARAging] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    if (companyId) {
      setSelectedBranch(String(companyId));
    } else if (canSelectAllBranches) {
      setSelectedBranch(ALL_BRANCHES_VALUE);
    }
  }, [companyId, canSelectAllBranches]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranch]);

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/companies');
      setCompanies(response.data || []);
    } catch (err) {
      console.error('Failed to fetch companies:', err);
    }
  };

  const handleBranchChange = (e) => {
    setSelectedBranch(e.target.value);
  };

  const getCompanyIdParam = () => {
    if (!canSelectAllBranches && !selectedBranch) return companyId || '';
    return selectedBranch === ALL_BRANCHES_VALUE ? '' : selectedBranch;
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const companyParam = getCompanyIdParam();
      const params = companyParam ? `?company_id=${companyParam}` : '';

      const [summaryRes, logsRes, topSpRes, topCustRes, branchRes] = await Promise.all([
        api.get(`/reports/summary${params}`),
        api.get('/reports/sync-logs?limit=5'),
        api.get(`/reports/top-salespersons${params}`),
        api.get(`/reports/top-customers${params}`),
        api.get(`/reports/dashboard-branch-summary${params}`)
      ]);

      const summary = summaryRes.data;
      const totalCollectibles = summary.reduce((sum, row) => sum + parseFloat(row.total_collectibles || 0), 0);
      const totalPostedCollected = summary.reduce((sum, row) => sum + parseFloat(row.posted_collected || 0), 0);
      const totalCollected = summary.reduce((sum, row) => sum + parseFloat(row.amount_collected || 0), 0);
      const totalPDC = summary.reduce((sum, row) => sum + parseFloat(row.pdc || 0), 0);
      const totalBalance = summary.reduce((sum, row) => sum + parseFloat(row.balance_collectibles || 0), 0);

      setStats({
        customers: new Set(summary.map((row) => row.customer_no)).size,
        totalCollectibles,
        totalPostedCollected,
        totalCollected,
        totalPDC,
        totalBalance
      });
      setLogs(logsRes.data);
      setTopSalespersons(topSpRes.data || []);
      setTopCustomers(topCustRes.data || []);
      setBranchSummary(branchRes.data || []);
    } catch (err) {
      console.error('Dashboard load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSyncCustomers = async () => {
    setSyncingCustomers(true);
    setMessage('');
    try {
      const response = await api.post('/bc-sync/sync/customers');
      setMessageType('success');
      setMessage(`✓ Sync completed. ${response.data.processed} customers updated.`);
      fetchData();
    } catch (err) {
      setMessageType('error');
      const errorMsg = err.response?.data?.message || err.response?.data?.error || 'Customer sync failed.';
      const details = err.response?.data?.details?.message;
      setMessage(details ? `${errorMsg}: ${details}` : errorMsg);
    } finally {
      setSyncingCustomers(false);
    }
  };

  const handleSyncLedger = async () => {
    setSyncingLedger(true);
    setMessage('');
    try {
      const response = await api.post('/bc-sync/sync/ledger');
      setMessageType('success');
      setMessage(`✓ Invoice sync completed. ${response.data.processed} ledger entries synced. AR Aging Details will now show data!`);
      fetchData();
    } catch (err) {
      setMessageType('error');
      const errorMsg = err.response?.data?.message || err.response?.data?.error || 'Ledger sync failed.';
      const details = err.response?.data?.details?.message;
      setMessage(details ? `${errorMsg}: ${details}` : errorMsg);
    } finally {
      setSyncingLedger(false);
    }
  };

  const handleFullSync = async () => {
    if (isFullSyncing) return;
    setIsFullSyncing(true);
    setMessage('');
    try {
      const response = await api.post('/bc-sync/sync/full-sync');
      if (response.data?.success) {
        setMessageType('success');
        setMessage('✓ Full sync from Business Central completed successfully!');
        fetchData();
      } else {
        setMessageType('error');
        setMessage('Sync completed with errors. Check server logs for details.');
      }
    } catch (err) {
      setMessageType('error');
      const errorMsg = err.response?.data?.message || err.response?.data?.error || 'Full sync failed.';
      setMessage(errorMsg);
    } finally {
      setIsFullSyncing(false);
    }
  };

  const handleSyncReports = async () => {
    if (isSyncingReports) return;
    setIsSyncingReports(true);
    setMessage('');
    try {
      // Sync company summary cache (for Summary Branch page)
      await api.post('/reports/manual-summary/refresh', { month: new Date().toISOString().slice(0, 7) });
      // Sync salesperson summary cache (for Salesperson Summary page)
      await api.post('/reports/salesperson-summary/refresh', { date: new Date().toISOString().slice(0, 10) });
      setMessageType('success');
      setMessage('✓ Reports cache synced successfully!');
      fetchData();
    } catch (err) {
      setMessageType('error');
      const errorMsg = err.response?.data?.message || err.message || 'Failed to sync reports cache.';
      setMessage(errorMsg);
    } finally {
      setIsSyncingReports(false);
    }
  };

  const handleSyncARAging = async () => {
    if (isSyncingARAging) return;
    setIsSyncingARAging(true);
    setMessage('');
    try {
      // Sync cached AR report (for AR Aging Details page)
      await api.post('/reports/cache-sync');
      setMessageType('success');
      setMessage('✓ AR Aging cache synced successfully!');
      fetchData();
    } catch (err) {
      setMessageType('error');
      const errorMsg = err.response?.data?.message || err.message || 'Failed to sync AR Aging cache.';
      setMessage(errorMsg);
    } finally {
      setIsSyncingARAging(false);
    }
  };

  if (loading) return <div className="loading">Loading dashboard...</div>;

  const formatCurrency = (value) => {
    if (value === undefined || value === null) return '0.00';
    const num = parseFloat(value);
    if (Number.isNaN(num)) return '0.00';
    return Math.abs(num).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatNumber = (value) => {
    if (value === undefined || value === null) return '0.00';
    const num = parseFloat(value);
    if (Number.isNaN(num)) return '0.00';
    return Math.abs(num).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const numberCellStyle = { fontWeight: 'bold' };

  const getRankStyle = (rank) => {
    const styles = {
      1: { background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)', color: '#000', border: '2px solid #B8860B' },
      2: { background: 'linear-gradient(135deg, #E8E8E8 0%, #C0C0C0 100%)', color: '#000', border: '2px solid #808080' },
      3: { background: 'linear-gradient(135deg, #CD7F32 0%, #8B4513 100%)', color: '#fff', border: '2px solid #654321' }
    };
    return styles[rank] || { background: '#e63740', color: '#fff', border: '2px solid #b71c1c' };
  };

  const isAdmin = currentUser && (currentUser.role === 'superadmin');

  // Chart calculations
  const totalChartValue = stats ? stats.totalCollected + stats.totalPDC + stats.totalBalance : 0;
  const collectedPercent = totalChartValue > 0 ? (stats.totalCollected / totalChartValue) * 100 : 0;
  const pdcPercent = totalChartValue > 0 ? (stats.totalPDC / totalChartValue) * 100 : 0;
  const balancePercent = totalChartValue > 0 ? (stats.totalBalance / totalChartValue) * 100 : 0;

  const maxBranchValue = Math.max(...branchSummary.map(b => parseFloat(b.total_collectibles || 0)), 1);

  return (
    <div className="dashboard-container">
      {/* Page Header */}
      <div className="page-header">
        <h1 className="dashboard-title">Dashboard</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          {canSelectAllBranches && (
            <select
              className="form-control"
              value={selectedBranch}
              onChange={handleBranchChange}
              style={{ minWidth: '180px', width: 'auto' }}
            >
              <option value={ALL_BRANCHES_VALUE}>All Branches</option>
              {companies.map((company) => (
                <option key={company.id} value={String(company.id)}>
                  {company.name}
                </option>
              ))}
            </select>
          )}
          
          {/* Sync Buttons */}
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={handleFullSync}
              disabled={isFullSyncing || syncingCustomers || syncingLedger}
              style={{ 
                padding: '10px 16px',
                background: isFullSyncing ? '#9ca3af' : '#2563eb',
                border: 'none',
                borderRadius: '6px',
                color: '#fff',
                cursor: isFullSyncing ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
                fontWeight: 500
              }}
              title="Full sync: customers + invoices + cache warming"
            >
              {isFullSyncing ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin 1s linear infinite' }}>
                  <line x1="12" y1="2" x2="12" y2="6" />
                  <line x1="12" y1="18" x2="12" y2="22" />
                  <line x1="4.93" y1="4.93" x2="7.76" y2="7.76" />
                  <line x1="16.24" y1="16.24" x2="19.07" y2="19.07" />
                  <line x1="2" y1="12" x2="6" y2="12" />
                  <line x1="18" y1="12" x2="22" y2="12" />
                  <line x1="4.93" y1="19.07" x2="7.76" y2="16.24" />
                  <line x1="16.24" y1="7.76" x2="19.07" y2="4.93" />
                </svg>
              ) : RefreshIcon}
              {isFullSyncing ? 'Syncing...' : 'Sync from BC'}
            </button>

            <button
              onClick={handleSyncReports}
              disabled={isSyncingReports}
              style={{ 
                padding: '10px 16px',
                background: isSyncingReports ? '#9ca3af' : '#059669',
                border: 'none',
                borderRadius: '6px',
                color: '#fff',
                cursor: isSyncingReports ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
                fontWeight: 500
              }}
              title="Sync Reports cache (Summary Branch & Salesperson Summary)"
            >
              {isSyncingReports ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin 1s linear infinite' }}>
                  <line x1="12" y1="2" x2="12" y2="6" />
                  <line x1="12" y1="18" x2="12" y2="22" />
                  <line x1="4.93" y1="4.93" x2="7.76" y2="7.76" />
                  <line x1="16.24" y1="16.24" x2="19.07" y2="19.07" />
                  <line x1="2" y1="12" x2="6" y2="12" />
                  <line x1="18" y1="12" x2="22" y2="12" />
                  <line x1="4.93" y1="19.07" x2="7.76" y2="16.24" />
                  <line x1="16.24" y1="7.76" x2="19.07" y2="4.93" />
                </svg>
              ) : RefreshIcon}
              {isSyncingReports ? 'Syncing...' : 'Sync Summary Reports'}
            </button>

            <button
              onClick={handleSyncARAging}
              disabled={isSyncingARAging}
              style={{ 
                padding: '10px 16px',
                background: isSyncingARAging ? '#9ca3af' : '#dc2626',
                border: 'none',
                borderRadius: '6px',
                color: '#fff',
                cursor: isSyncingARAging ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
                fontWeight: 500
              }}
              title="Sync AR Aging Details cache"
            >
              {isSyncingARAging ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin 1s linear infinite' }}>
                  <line x1="12" y1="2" x2="12" y2="6" />
                  <line x1="12" y1="18" x2="12" y2="22" />
                  <line x1="4.93" y1="4.93" x2="7.76" y2="7.76" />
                  <line x1="16.24" y1="16.24" x2="19.07" y2="19.07" />
                  <line x1="2" y1="12" x2="6" y2="12" />
                  <line x1="18" y1="12" x2="22" y2="12" />
                  <line x1="4.93" y1="19.07" x2="7.76" y2="16.24" />
                  <line x1="16.24" y1="7.76" x2="19.07" y2="4.93" />
                </svg>
              ) : RefreshIcon}
              {isSyncingARAging ? 'Syncing...' : 'Sync AR Aging Details'}
            </button>

            <button
              onClick={handleSyncCustomers}
              disabled={syncingCustomers}
              style={{ 
                padding: '10px 16px',
                background: syncingCustomers ? '#999' : '#17a2b8',
                border: 'none',
                borderRadius: '6px',
                color: '#fff',
                cursor: syncingCustomers ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
                fontWeight: 500
              }}
            >
              {RefreshIcon}
              {syncingCustomers ? 'Syncing...' : 'Sync Customers'}
            </button>
            
            <button
              onClick={handleSyncLedger}
              disabled={syncingLedger}
              style={{ 
                padding: '10px 16px',
                background: syncingLedger ? '#999' : '#e63740',
                border: 'none',
                borderRadius: '6px',
                color: '#fff',
                cursor: syncingLedger ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
                fontWeight: 500,
                boxShadow: '0 2px 4px rgba(230,55,64,0.3)'
              }}
            >
              {RefreshIcon}
              {syncingLedger ? 'Syncing...' : 'Sync Invoices (AR Aging)'}
            </button>
          </div>
        </div>
      </div>

      {/* Success Message */}
      {message && (
        <div className={`alert ${messageType === 'success' ? 'alert-success' : 'alert-error'}`}>
          {message}
        </div>
      )}

      {/* Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card border-blue">
          <h3>Total Customers</h3>
          <p>{stats?.customers || 0}</p>
        </div>
        <div className="stat-card border-red">
          <h3>Total Collectibles</h3>
          <p>{formatCurrency(stats?.totalCollectibles)}</p>
        </div>
        <div className="stat-card border-purple">
          <h3>Posted Amount Collected Total ERP</h3>
          <p>{formatCurrency(stats?.totalPostedCollected)}</p>
        </div>
        <div className="stat-card border-green">
          <h3>Amount Collected</h3>
          <p>{formatCurrency(stats?.totalCollected)}</p>
        </div>
        <div className="stat-card border-yellow">
          <h3>PDC</h3>
          <p>{formatCurrency(stats?.totalPDC)}</p>
        </div>
      </div>

      {/* Charts Section */}
      <div className="section-card">
        <div className="section-header">
          <h3 className="section-title">Collection Overview</h3>
        </div>
        <div className="section-body">
          <div className="dashboard-charts-grid">
            {/* Donut Chart - Collection Breakdown */}
            <div className="chart-card">
              <h4 className="chart-title">Collection Breakdown</h4>
              {totalChartValue > 0 ? (
                <div className="chart-donut-wrapper">
                  <div
                    className="chart-donut"
                    style={{
                      background: `conic-gradient(
                        #28a745 0deg ${collectedPercent * 3.6}deg,
                        #ffca10 ${collectedPercent * 3.6}deg ${(collectedPercent + pdcPercent) * 3.6}deg,
                        #e63740 ${(collectedPercent + pdcPercent) * 3.6}deg 360deg
                      )`
                    }}
                  >
                    <div className="chart-donut-center">
                      <span className="chart-donut-value">{formatCurrency(stats.totalCollectibles)}</span>
                      <span className="chart-donut-label">Total</span>
                    </div>
                  </div>
                  <div className="chart-legend">
                    <div className="chart-legend-item">
                      <span className="chart-legend-dot" style={{ background: '#28a745' }} />
                      <span>Collected {formatCurrency(stats.totalCollected)}</span>
                    </div>
                    <div className="chart-legend-item">
                      <span className="chart-legend-dot" style={{ background: '#ffca10' }} />
                      <span>PDC {formatCurrency(stats.totalPDC)}</span>
                    </div>
                    <div className="chart-legend-item">
                      <span className="chart-legend-dot" style={{ background: '#e63740' }} />
                      <span>Balance {formatCurrency(stats.totalBalance)}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bordered-card"><p>No collection data available</p></div>
              )}
            </div>

            {/* Branch Comparison Bar Chart */}
            <div className="chart-card">
              <h4 className="chart-title">Branch Comparison</h4>
              {branchSummary.length > 0 ? (
                <div className="chart-horizontal-bars">
                  {branchSummary.map((branch, idx) => {
                    const widthPercent = (parseFloat(branch.total_collectibles || 0) / maxBranchValue) * 100;
                    return (
                      <div key={idx} className="chart-bar-row">
                        <div className="chart-bar-label" title={branch.company_name}>
                          {branch.company_name}
                        </div>
                        <div className="chart-bar-track">
                          <div
                            className="chart-bar-fill"
                            style={{ width: `${widthPercent}%` }}
                          />
                        </div>
                        <div className="chart-bar-value">{formatNumber(branch.total_collectibles)}</div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="bordered-card"><p>No branch data available</p></div>
              )}
            </div>

            {/* Top Salespersons Mini Bar Chart */}
            <div className="chart-card">
              <h4 className="chart-title">Top Salespersons by Collection</h4>
              {topSalespersons.length > 0 ? (
                <div className="chart-vertical-bars">
                  {topSalespersons.slice(0, 8).map((sp, idx) => {
                    const maxHeight = Math.max(...topSalespersons.slice(0, 8).map(s => s.total_amount_collected || s.total_collectibles));
                    const value = sp.total_amount_collected || 0;
                    const heightPercentage = maxHeight > 0 ? (value / maxHeight) * 100 : 0;
                    return (
                      <div key={idx} className="chart-vbar-item">
                        <div className="chart-vbar-track">
                          <div
                            className="chart-vbar-fill"
                            style={{ height: `${Math.max(heightPercentage, 4)}%` }}
                            title={`${sp.salesperson_name}: ${formatNumber(value)}`}
                          />
                        </div>
                        <div className="chart-vbar-label" title={sp.salesperson_name}>
                          {sp.salesperson_name.split(' ')[0]}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="bordered-card"><p>No salesperson data available</p></div>
              )}
            </div>

            {/* Collection Efficiency Chart */}
            <div className="chart-card">
              <h4 className="chart-title">Collection Efficiency</h4>
              {topCustomers.length > 0 ? (
                <div className="chart-progress-list">
                  {topCustomers.slice(0, 6).map((cust, idx) => {
                    const collectionPercent = Math.min(parseFloat(cust.collection_percentage) || 0, 100);
                    const statusColor = collectionPercent >= 100 ? '#28a745' :
                                       collectionPercent >= 75 ? '#5a9fd4' :
                                       collectionPercent >= 50 ? '#ffca10' : '#e63740';
                    return (
                      <div key={idx} className="chart-progress-item">
                        <div className="chart-progress-header">
                          <strong title={cust.customer_name}>{cust.customer_name}</strong>
                          <span style={{ color: statusColor, fontWeight: 'bold' }}>{collectionPercent}%</span>
                        </div>
                        <div className="chart-progress-track">
                          <div
                            className="chart-progress-fill"
                            style={{ width: `${collectionPercent}%`, background: statusColor }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="bordered-card"><p>No customer data available</p></div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Recent Sync Activity Section - Admin Only */}
      {isAdmin && (
        <div className="section-card">
          <div className="section-header">
            <h3 className="section-title">Recent Sync Activity</h3>
            <span className="section-badge">{logs.length}</span>
          </div>
          <div className="section-body">
            {logs.length === 0 ? (
              <div className="bordered-card">
                <div className="empty-state-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ced4da" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                </div>
                <h4 className="empty-state-title">No recent activity</h4>
                <p className="empty-state-text">Sync data will appear here</p>
              </div>
            ) : (
              <table className="table table-compact">
                <thead>
                  <tr>
                    <th>Source</th>
                    <th>Status</th>
                    <th>Records</th>
                    <th>Message</th>
                    <th>Time</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.slice(0, 10).map((row, index) => (
                    <tr key={index}>
                      <td><strong>{row.source}</strong></td>
                      <td>
                        <span className={`badge ${row.status === 'success' ? 'badge-success' : 'badge-danger'}`}>
                          {row.status}
                        </span>
                      </td>
                      <td>{row.records_processed}</td>
                      <td>{row.message || '-'}</td>
                      <td>{new Date(row.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Top 10 Salesperson Collectors */}
      <div className="section-card">
        <div className="section-header">
          <h3 className="section-title">Top 10 Salesperson Collectors</h3>
          <span className="section-badge">Leaderboard</span>
        </div>
        <div className="section-body">
          {topSalespersons.length === 0 ? (
            <div className="bordered-card">
              <p>No salesperson data available</p>
            </div>
          ) : (
            <table className="table table-compact">
              <thead>
                <tr>
                  <th style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Rank</th>
                  <th style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Salesperson</th>
                  <th style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Customers</th>
                  <th style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Total Collectibles</th>
                  <th style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Amount Collected</th>
                  <th style={{ backgroundColor: '#e63740', color: '#ffffff' }}>PDC</th>
                  <th style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Collection %</th>
                </tr>
              </thead>
              <tbody>
                {topSalespersons.map((sp, index) => (
                  <tr key={index}>
                    <td>
                      <span style={{
                        fontWeight: 'bold',
                        padding: '4px 10px',
                        borderRadius: '50%',
                        display: 'inline-block',
                        fontSize: '0.85rem',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        ...getRankStyle(sp.rank)
                      }}>{sp.rank}</span>
                    </td>
                    <td><strong>{sp.salesperson_name}</strong></td>
                    <td style={numberCellStyle}>{sp.customer_count}</td>
                    <td style={{ ...numberCellStyle, color: '#5a9fd4' }}>{formatNumber(sp.total_collectibles)}</td>
                    <td style={{ ...numberCellStyle, color: '#28a745' }}>{formatNumber(sp.total_amount_collected)}</td>
                    <td style={numberCellStyle}>{formatNumber(sp.total_pdc)}</td>
                    <td style={numberCellStyle}><strong>{sp.collection_percentage}%</strong></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Top 10 Highest Customers by Invoice Total */}
      <div className="section-card">
        <div className="section-header">
          <h3 className="section-title">Top 10 Highest Customers</h3>
          <span className="section-badge">By Invoice Total</span>
        </div>
        <div className="section-body">
          {topCustomers.length === 0 ? (
            <div className="bordered-card">
              <p>No customer data available</p>
            </div>
          ) : (
            <table className="table table-compact">
              <thead>
                <tr>
                  <th style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Rank</th>
                  <th style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Customer No.</th>
                  <th style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Customer Name</th>
                  <th style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Total Collectibles</th>
                  <th style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Amount Collected</th>
                  <th style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Balance</th>
                  <th style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Collection %</th>
                </tr>
              </thead>
              <tbody>
                {topCustomers.map((cust, index) => (
                  <tr key={index}>
                    <td>
                      <span style={{
                        fontWeight: 'bold',
                        padding: '4px 10px',
                        borderRadius: '50%',
                        display: 'inline-block',
                        fontSize: '0.85rem',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        ...getRankStyle(cust.rank)
                      }}>{cust.rank}</span>
                    </td>
                    <td style={numberCellStyle}>{cust.customer_no}</td>
                    <td><strong>{cust.customer_name}</strong></td>
                    <td style={{ ...numberCellStyle, color: '#5a9fd4' }}>{formatNumber(cust.total_collectibles)}</td>
                    <td style={{ ...numberCellStyle, color: '#28a745' }}>{formatNumber(cust.amount_collected)}</td>
                    <td style={{ ...numberCellStyle, color: cust.balance_collectible > 0 ? '#e63740' : '#28a745' }}>{formatNumber(cust.balance_collectible)}</td>
                    <td style={numberCellStyle}><strong>{cust.collection_percentage}%</strong></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
